import { useTranslation } from "react-i18next";
import { useCallback, useRef, type KeyboardEvent } from "react";
import { cn } from "../../utils/cn";
import { Icon } from "./Icon";

export type SegmentedOption = {
  id: string;
  label: string;
  /**
   * Optional leading icon (Iconify name, e.g. `mdi:linux`) rendered before the
   * label. Omit for a text-only segment — the default for every existing
   * consumer.
   */
  icon?: string;
};

type SegmentedControlProps = {
  options: SegmentedOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /** Accessible label for the control group */
  ariaLabel?: string;
  /**
   * Unique prefix for generated tab/panel ids. When set, each segment gets an
   * `id` and `aria-controls` that pair with `getTabPanelProps(idBase, value)` on
   * the linked panel (same scheme as `Tabs`). Optional — omit for a plain switch.
   */
  idBase?: string;
  /**
   * When true the whole group is non-interactive: every segment reports
   * `aria-disabled`, drops out of the tab order, and ignores clicks and arrow
   * keys. The selected segment still reads as selected so the value stays clear.
   * Omit for a normal interactive control.
   */
  disabled?: boolean;
  /**
   * Label casing. `"upper"` (default) keeps the established `uppercase
   * tracking-wide` segment styling used by every existing consumer; `"normal"`
   * renders labels as authored (e.g. proper-noun platform names like `macOS`).
   * Opt-in only — omit to leave current callsites unchanged.
   */
  caseStyle?: "upper" | "normal";
};

export const SegmentedControl = ({
  options,
  value,
  onChange,
  className,
  ariaLabel,
  idBase,
  disabled = false,
  caseStyle = "upper",
}: SegmentedControlProps) => {
  const { t } = useTranslation("common");
  const containerRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      if (disabled) return;
      let nextIndex: number | null = null;

      switch (event.key) {
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          nextIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
          break;
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
          break;
        case "Home":
          event.preventDefault();
          nextIndex = 0;
          break;
        case "End":
          event.preventDefault();
          nextIndex = options.length - 1;
          break;
      }

      if (nextIndex !== null) {
        const nextOption = options[nextIndex];
        if (nextOption) {
          onChange(nextOption.id);
          // Focus the new tab
          const buttons = containerRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
          buttons?.[nextIndex]?.focus();
        }
      }
    },
    [options, onChange, disabled]
  );

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel ?? t($ => $.timeRange)}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border border-border/70 bg-muted/80 p-1 shadow-subtle",
        className
      )}
    >
      {options.map((option, index) => {
        const isActive = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            {...(idBase
              ? { id: `${idBase}-tab-${option.id}`, "aria-controls": `${idBase}-tabpanel-${option.id}` }
              : {})}
            aria-selected={isActive}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : isActive ? 0 : -1}
            className={cn(
              "px-3 py-1.5 text-xs font-medium rounded-md transition-all",
              option.icon && "inline-flex items-center justify-center gap-1.5",
              caseStyle === "upper" && "uppercase tracking-wide",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              disabled && "cursor-default",
              isActive
                ? "bg-card text-foreground shadow-subtle border border-border"
                : cn("text-muted-foreground", !disabled && "hover:text-foreground")
            )}
            onClick={disabled ? undefined : () => onChange(option.id)}
            onKeyDown={disabled ? undefined : (e) => handleKeyDown(e, index)}
          >
            {option.icon && <Icon icon={option.icon} className="h-4 w-4 shrink-0" aria-hidden />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
